import {
  Button, Flex, Text, PinInput
} from '@huspy/briks-web';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import useGetOpportunityId from '@modules/core/hooks/useGetOpportunityId';
import useOTPTimer from '@modules/core/hooks/useOtpTimer';
import { useRequestOtp } from '@modules/core/hooks/mutations/useRequestOtp';
import { useVerifyOtp } from '@modules/core/hooks/mutations/useVerifyOtp';
import { VaultException } from '@shared/exceptions';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { USER_EVENTS } from '@shared/analytics/events';
import { useAuthStore } from '@modules/core/store';
import { AuthMethod } from '@modules/core/api/const';

const OTP_LENGTH = 6;

const Resend = ({
  setSession,
  resetOtp,
  isExpired,
  selectedAuthMethod,
}: {
  resetOtp: () => void;
  setSession: (session: string) => void;
  isExpired: boolean;
  selectedAuthMethod: AuthMethod;
}) => {
  const oppId = useGetOpportunityId();
  const { t } = useTranslation();
  const { timeLeft, isResendEnabled, startTimer } = useOTPTimer(30);
  const { mutateAsync } = useRequestOtp(oppId!, true);
  const { getClientId } = useAuthStore();

  const handleReset = async () => {
    if (!isResendEnabled) return;
    trackAmplitudeEvent(
      USER_EVENTS.AUTH.VAULT_RESEND_OTP_CLICKED,
      {
        client_id: getClientId(),
        method: selectedAuthMethod,
      }
    );
    resetOtp();
    startTimer();
    const { session } = await mutateAsync(selectedAuthMethod);
    setSession(session);
  };

  const textToRender = () => {
    if (timeLeft !== 0) {
      return t('auth.verifyOtp.resendIn', { sec: timeLeft });
    }
    if (isExpired) {
      return t('auth.verifyOtp.sendNewCode');
    }
    if (isResendEnabled) {
      return t('auth.verifyOtp.resend');
    }
    return t('auth.verifyOtp.resendIn', { sec: timeLeft });
  };

  return (
    <Button
      variant='ghost'
      disabled={ !isResendEnabled }
      size='md'
      p='1.5'
      ml='1'
      h='28px'
    >
      <Text
        data-testid='otp-resend-btn'
        color='neutral.500'
        fontWeight='semibold'
        cursor='pointer'
        onClick={ handleReset }
      >
        {textToRender()}
      </Text>
    </Button>
  );
};

const unBlurLastInput = () => {
  const input = document?.getElementById('pin-input:otp-pin-input:5');
  if (input) {
    input.blur();
  }
};

export const VerifyOtp = ({
  session,
  setSession,
  sendTo,
  selectedAuthMethod,
  tryAnotherMethod,
  isSmsAuthMethodAvailable,
}: {
  session: string | null;
  setSession: (session: string | null) => void;
  sendTo: string;
  tryAnotherMethod: () => void;
  selectedAuthMethod: AuthMethod;
  isSmsAuthMethodAvailable?: boolean;
}) => {
  const { t } = useTranslation();
  const [otp, setOtp] = useState<string[]>([]);
  const oppId = useGetOpportunityId();
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const { mutateAsync, reset: resetApi } = useVerifyOtp(oppId!, selectedAuthMethod);
  const { getClientId } = useAuthStore();

  const isOtpExpired = !!error && !session;

  const isDisabled = otp.filter(Boolean).length !== OTP_LENGTH || !session;

  const onComplete = async (value: string) => {
    if (session) {
      try {
        setIsPending(true);
        trackAmplitudeEvent(
          USER_EVENTS.AUTH.VAULT_OTP_LOGIN_ATTEMPTED,
          {
            client_id: getClientId(),
            method: selectedAuthMethod,
          }
        );
        await mutateAsync({ otp: value, session });
      } catch (_error) {
        setOtp(new Array(OTP_LENGTH).fill(''));
        unBlurLastInput();
        const errorData = (_error as VaultException)?.data;
        const errorMessage = errorData?.session
          ? t('auth.verifyOtp.inValidCode')
          : t('auth.verifyOtp.tooManyAttempts');
        if (errorData?.session) {
          setSession(errorData.session);
          setError(errorMessage);
        } else {
          setError(errorMessage);
          setSession(null);
        }
        trackAmplitudeEvent(USER_EVENTS.AUTH.VAULT_OTP_LOGIN_FAILED, {
          client_id: getClientId(),
          error_message: errorMessage,
          method: selectedAuthMethod,
        });
      } finally {
        setIsPending(false);
      }
    }
  };

  const reset = () => {
    resetApi();
    setError(null);
  };

  const resetOtp = () => {
    setOtp(new Array(OTP_LENGTH).fill(''));
    reset();
  };

  return (
    <Flex
      border='1px solid'
      borderColor='neutral.100'
      borderRadius='4'
      p='8'
      gap='4'
      direction='column'
      shadow='shadow-4'
      width='100%'
      maxWidth='500'
    >
      <Flex w='100%' direction='column' gap='1'>
        <Text size='5xl' fontWeight='medium'>
          {t('auth.verifyOtp.welcome')}
        </Text>
        <Text size='lg' color='text-secondary' fontWeight='medium'>
          {t('auth.verifyOtp.instructions')}
        </Text>
        <Text size='lg' fontWeight='medium'>
          {sendTo}
        </Text>
      </Flex>
      <Flex w='100%' align='center' justify='center' direction='column' gap='2'>
        <Text size='sm' alignSelf='self-start' color='text-secondary'>
          {t('auth.verifyOtp.enterCode')}
        </Text>
        <PinInput
          id='otp-pin-input'
          data-testid='otp-pin-input'
          type='numeric'
          onValueChange={ (value) => {
            setOtp(value.value);
            if (error && !isOtpExpired) {
              reset();
            }
          } }
          value={ otp }
          length={ OTP_LENGTH }
          placeholder=''
          gap='lg'
          size='lg'
          otp
          onValueComplete={ (value) => onComplete(value.valueAsString) }
          invalid={ !!error }
        />
        {error && (
          <Text size='sm' color='text-error-primary'>
            {error}
          </Text>
        )}
      </Flex>
      <Flex w='100%' direction='column' gap='0'>
        <Flex align='center'>
          {!isOtpExpired && (
            <Text color='neutral.500'>{t('auth.verifyOtp.error')}</Text>
          )}
          <Resend
            resetOtp={ resetOtp }
            setSession={ setSession }
            isExpired={ isOtpExpired }
            selectedAuthMethod={ selectedAuthMethod }
          />
        </Flex>
        { isSmsAuthMethodAvailable
        && (
          <Flex align='center' gap='1'>
            <Text color='neutral.500'>{t('auth.verifyOtp.haveTrouble')}</Text>
            <Button
              data-testid='otp-try-another-method-btn'
              variant='ghost'
              p='1'
              h='28px'
              onClick={ tryAnotherMethod }
            >
              <Text color='neutral.500' fontWeight='semibold'>
                {t('auth.verifyOtp.tryAnotherMethod')}
              </Text>
            </Button>
          </Flex>
        )}
      </Flex>
      <Button
        data-testid='otp-verify-btn'
        loading={ isPending }
        disabled={ isDisabled }
        variant={ isDisabled ? 'secondary' : 'primary' }
        onClick={ () => onComplete(otp.join('')) }
      >
        {t('auth.verifyOtp.sendButton')}
      </Button>
      <Flex justify='center' align='center' gap='1'>
        <Text color='neutral.500' size='sm'>
          {t('auth.poweredBy')}
        </Text>
        <Text color='neutral.500' fontWeight='semibold'>
          Client Vault
        </Text>
      </Flex>
    </Flex>
  );
};
